import { useEffect, useState } from 'react';
import { AppStyles } from './styles/App';
import { RALColors, options } from './_data';
import { GlobalStyles } from './styles';
import { IOption, Lang, MyContentWindow, modeStateType } from './_types';
import ToggleList from './components/ToggleList';
import MainModal, { IFormikValues } from './components/MainModal';
import { getProcedures } from './services/utils';
import axios from 'axios';
import SuccessPopup from './components/SuccessPopup';

const initState: modeStateType = {
  model: 'standard', // [panorama, standard, individual. topProfessional ]
  grassType: 'texturized', // ['monofilament', 'fibered', 'texturized']
  grassColor: '#3366ff',
  frameColor: '#0e0e0f',
  meshColor: '#0e0e0f',
  lightColor: '#0e0e0f',
  illuminationPillars: 'cShape', // ['none', 'cShape', 'vShape', 'straight']
  roofing: 'none', // ['none', 'cover', 'hangar']
  benches: 'none', // ['none', 'left', 'right', 'both']
  doorType: '2DoorNonProtection', // ['2DoorNonProtection', '2DoorProtection', 'slidingDoor', 'regularDoor']
  doorLocation: 'both', //['left', 'right', 'both'],
  protection: [], //['left', 'right', 'back', 'front'],
  personalizedNet: false,
  personalizedNetImage: '',
};

type modeStateTypePatial = {
  [P in keyof modeStateType]?: modeStateType[P]; // Make all properties optional using mapped type
};

const prsNetIndex = 10; // index of personalize net

const url = 'https://us-central1-padel-magic.cloudfunctions.net/sendEmail';

function App() {
  const [lang, setLang] = useState<Lang>('es');
  const [curIndex, setCurIndex] = useState<number>(0);
  const [modelState, setModelState] = useState<modeStateType>(initState);
  const [show, setShow] = useState(false); // information form
  const [isShowPopup, setIsShowPopup] = useState(false); // successful popup
  const [loading, setLoading] = useState(false);
  const handleClose = () => {
    setShow(false);
    setError('');
  };
  const handleShow = () => setShow(true);
  const [error, setError] = useState<string>('');

  const takeScreenShot = (values: IFormikValues) => {
    const procedures = getProcedures();
    if (procedures) {
      if (procedures?.takeScreenShot && procedures?.getScreenshot) {
        setLoading(true);
        procedures?.takeScreenShot();
        setTimeout(() => {
          const screenShot = base64URLtoFile(
            procedures.getScreenshot(),
            'screenshot.png'
          );
          const newState: modeStateTypePatial = { ...modelState };

          Object.keys(newState).forEach((key) => {
            const newKey = key as keyof modeStateTypePatial;
            if (newKey.includes('Color')) {
              if (newKey === 'meshColor') {
                RALColors.forEach((color) => {
                  if (color.hexValue === newState[newKey]) {
                    newState[newKey] = color.name;
                  }
                });
              } else {
                options.forEach((option: IOption) => {
                  if (option.value === newKey) {
                    option.data.forEach((data) => {
                      if (data.value === newState[newKey]) {
                        if (
                          newKey === 'grassColor' ||
                          newKey === 'lightColor' ||
                          newKey === 'frameColor'
                        ) {
                          newState[newKey] = data.name;
                        }
                      }
                    });
                  }
                });
              }
            }
          });

          const formData = new FormData();

          const submitedData = {
            ...newState,
            ...values,
            screenShot,
          };

          Object.entries(submitedData).forEach(([key, value]) => {
            if (key === 'illuminationPillars') {
              key = 'lightPostType';
            } else if (key === 'lightColor') {
              key = 'lightPostColor';
            }
            if (key === 'screenShot' || key === 'personalizedNetImage') {
              if (value) {
                formData.append(key, value as File);
              } else {
                formData.append(key, value as string);
              }
            } else if (Array.isArray(value)) {
              formData.append(key, value.toString());
            } else if (typeof value === 'boolean') {
              formData.append(key, value ? 'true' : 'false');
            } else {
              formData.append(key, value as string);
            }
          });

          axios
            .post(url, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Access-Control-Allow-Origin': '*',
              },
            })
            .then((response) => {
              handleClose();
              setIsShowPopup(true);
              // console.log(response);
            })
            .catch((error) => {
              setError('Error sending form. Please try again.');
              console.error(error);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 700);
      }
    }
  };

  useEffect(() => {
    if (isShowPopup) {
      const timer = setTimeout(() => {
        setIsShowPopup(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isShowPopup]);

  function base64URLtoFile(base64String: string, filename: string) {
    try {
      if (!base64String.startsWith('data:')) {
        throw new Error('Invalid data URL format');
      }
      // Pre-process to remove redundant parts (assuming image data URL)
      const parts = base64String.split(';');
      if (parts.length < 2) {
        throw new Error('Invalid data URL format');
      }
      const mimeType = parts[0].split(':')[1].trim(); // Extract and trim MIME type
      const base64Data = parts[1].split(',')[1]; // Extract base64 data
      const bstr = atob(base64Data);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mimeType });
    } catch (error) {
      console.log(error);
    }
  }

  const handleUpdateState = (
    valueName: keyof modeStateType,
    valueData: string | number | boolean,
    file?: File
  ) => {
    const procedures = getProcedures();
    if (procedures) {
      const capitalizedStr =
        valueName.charAt(0).toUpperCase() + valueName.slice(1);
      const puzzleFunc: string = 'change' + capitalizedStr;
      if (procedures?.[puzzleFunc]) {
        procedures?.[puzzleFunc](valueData);
      }
      setModelState((oldState: modeStateType) => {
        if (valueName === 'protection') {
          let clonedProtection: (string | number | boolean)[] = [];
          if (valueData === 'none') {
            return { ...oldState, [valueName]: clonedProtection };
          } else if (valueData === 'all') {
            return {
              ...oldState,
              [valueName]: ['left', 'right', 'back', 'front'],
            };
          } else {
            clonedProtection = [...modelState.protection];
            const index = clonedProtection.findIndex(
              (val) => val === valueData
            );
            if (index > -1) {
              clonedProtection.splice(index, 1);
            } else {
              clonedProtection.push(valueData);
            }
          }
          return { ...oldState, [valueName]: clonedProtection };
        } else {
          if (file) {
            return { ...oldState, [valueName]: file };
          } else {
            return { ...oldState, [valueName]: valueData };
          }
        }
      });
    }
  };

  const handleChangeIndex = (index: number) => {
    if (curIndex === index && index !== prsNetIndex) {
      setCurIndex(-1);
    } else {
      setCurIndex(index);
      const iframe = document.querySelector('#main-frame') as HTMLIFrameElement;
      if (iframe) {
        const windowFrame = iframe.contentWindow as MyContentWindow;
        if (windowFrame) {
          const procedures = windowFrame?.v3d?.puzzles?.procedures;
          if (procedures && procedures?.tweenCamera) {
            if (index === prsNetIndex && curIndex !== prsNetIndex) {
              procedures.tweenCamera('pos1', 'tar1');
            } else if (curIndex === prsNetIndex) {
              procedures.tweenCamera('pos2', 'tar1');
              if (index === prsNetIndex) {
                setCurIndex(-1);
              }
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const findNameWithValue = (
    optionName: string,
    value: string | boolean | (string | number | boolean)[],
    isText?: boolean
  ) => {
    let name = '';
    if (typeof value === 'string') {
      options.forEach((option: IOption) => {
        if (option.name === optionName) {
          option.data?.forEach((data) => {
            if (data.value === value) {
              name = data.lang?.[lang] || data.name;
            }
          });
        }
      });
      if (optionName === 'mesh color' && !name) {
        RALColors.forEach((color) => {
          if (color.hexValue === value) {
            name = color.name;
          }
        });
      }
    } else if (typeof value === 'boolean') {
      if (value) {
        name = lang === 'en' ? 'Yes' : 'Tener';
      } else {
        name = lang === 'en' ? 'No' : 'Todo';
      }
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        name = lang === 'en' ? 'None' : 'Ninguna';
      } else if (value.length === 4) {
        name = lang === 'en' ? 'All' : 'Todo';
      } else {
        value.forEach((val) => {
          options.forEach((option: IOption) => {
            if (option.name === optionName) {
              option.data?.forEach((data) => {
                if (data.value === val) {
                  name += (data.lang?.[lang] || data.name) + ', ';
                }
              });
            }
          });
        });
        name = name.slice(0, -2);
      }
    }
    return isText ? (
      name
    ) : (
      <div>
        <i className="fa-solid fa-arrow-right-long"></i> {name}
      </div>
    );
  };

  return (
    <AppStyles className="App">
      <GlobalStyles />
      <div className="app__container">
        <div
          id="frame__container"
          className="model__container position__sticky"
        >
          <iframe
            id="main-frame"
            className="main__frame"
            title="main-frame"
            src="./model3d/4292_Pista_padel/index.html"
          ></iframe>
        </div>

        <div className="configurator__container">
          <div className="configurator__main">
            <div className="languages__container">
              <div
                onClick={() => setLang('en')}
                className={`language__item ${lang === 'en' ? 'active' : ''}`}
              >
                <img src="./images/uk-icon.png" alt="flag-uk" />
              </div>
              <div
                onClick={() => setLang('es')}
                className={`language__item ${lang === 'es' ? 'active' : ''}`}
              >
                <img src="./images/spain-icon.png" alt="flag-uk" />
              </div>
            </div>
            <div className="confiurator__values__container">
              {options?.map(
                (option: IOption, index: number, array: IOption[]) => {
                  let cornerIndex: number = -1;
                  const cornerChecking = false;
                  const cornerModelChecking = true;
                  return (
                    <div
                      key={option?.name}
                      className={`option__container ${
                        cornerModelChecking ? '' : 'hidden'
                      }`}
                    >
                      <div className="option__main__container">
                        <div
                          className="toggle__tag"
                          onClick={() => handleChangeIndex(index)}
                        >
                          <span className="option__number">
                            {cornerIndex > -1 &&
                            cornerChecking &&
                            index > cornerIndex
                              ? index
                              : index + 1}
                          </span>
                          <span className="tag__text">
                            {option?.lang[lang].toUpperCase()}
                          </span>
                        </div>
                        <div
                          style={(() => {
                            const length = option?.data?.length;
                            let col = 3;
                            if (
                              option.selectionType === 'listOption' ||
                              option.selectionType === 'toggle-1' ||
                              option.selectionType === 'multi-dropdown'
                            ) {
                              col = 1;
                            }
                            const row = Math.ceil(
                              (length + 0) / col
                              // (length + (option.value === 'frameColor' ? 0 : 0)) / col
                            );
                            const height =
                              row * 55 +
                              59 +
                              (option.selectionType === 'multi-dropdown' ||
                              option.value === 'personalizedNet'
                                ? // option.value === 'personalizedNet'
                                  55
                                : 0) +
                              (option.value === 'meshColor' ? 56 : 0);
                            if (curIndex === index)
                              return {
                                height: height.toString() + 'px',
                              };
                            else return {};
                          })()}
                          className={`toggle__container ${
                            index === curIndex ? 'display' : ''
                          }`}
                        >
                          <ToggleList
                            handleUpdateState={handleUpdateState}
                            modelState={modelState}
                            value={option.value}
                            option={option}
                            type={option.selectionType}
                            lang={lang}
                          ></ToggleList>
                          <div className="buttons__container">
                            {index > 0 ? (
                              <div
                                className="button__prev"
                                onClick={() => handleChangeIndex(index - 1)}
                              >
                                <i className="fa-solid fa-angle-up"></i>
                                {lang === 'es' ? 'Anterior' : 'Previous'}
                              </div>
                            ) : (
                              ''
                            )}
                            {index < array.length - 1 ? (
                              <div
                                className="button__next"
                                onClick={() => handleChangeIndex(index + 1)}
                              >
                                {lang === 'es' ? 'Siguiente' : 'Next'}
                                <i className="fa-solid fa-angle-down"></i>
                              </div>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        <div
                          title={
                            findNameWithValue(
                              option.name,
                              modelState[option.value],
                              true
                            ) as string
                          }
                          className={`selecting__value ${
                            curIndex !== index ? 'show' : ''
                          }`}
                        >
                          {findNameWithValue(
                            option.name,
                            modelState[option.value]
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
            <div className="submit__button__container">
              <button
                className={`submit__button`}
                onClick={handleShow}
                // onClick={loading ? () => {} : () => takeScreenShot()}
              >
                {lang === 'es' ? 'Descargar proyecto' : 'Send Project'}
              </button>
            </div>
          </div>
          <>
            <MainModal
              error={error}
              lang={lang}
              isLoading={loading}
              takeScreenshot={takeScreenShot}
              state={modelState}
              show={show}
              handleClose={handleClose}
            ></MainModal>
          </>
          <>
            <SuccessPopup
              show={isShowPopup}
              handleClose={() => setIsShowPopup(false)}
            ></SuccessPopup>
          </>
        </div>
      </div>
    </AppStyles>
  );
}

export default App;
